/**
 * Settings Page - Client Portal
 * Allows users to manage their email notification preferences
 */
import React, { useState, useEffect } from 'react';
import { auth } from '../auth';
import './Settings.css';

interface EmailPreferences {
  emailEnabled: boolean;
  briefingFrequency: 'daily' | 'weekly' | 'none';
  monitoringFrequency: 'twice_daily' | 'daily' | 'weekly' | 'none';
}

const Settings: React.FC = () => {
  const [preferences, setPreferences] = useState<EmailPreferences>({
    emailEnabled: true,
    briefingFrequency: 'daily',
    monitoringFrequency: 'twice_daily'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const user = auth.getCurrentUser();
      if (!user) return;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/portal/users/me`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setPreferences({
            emailEnabled: data.user.emailEnabled ?? true,
            briefingFrequency: data.user.briefingFrequency || 'daily',
            monitoringFrequency: data.user.monitoringFrequency || 'twice_daily'
          });
        }
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const user = auth.getCurrentUser();
      if (!user) return;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/portal/users/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(preferences)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Email preferences saved successfully!' });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save preferences' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save preferences. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const getFrequencyDescription = (type: 'briefing' | 'monitoring', frequency: string) => {
    if (type === 'briefing') {
      switch (frequency) {
        case 'daily': return 'Every morning at 8:00 AM';
        case 'weekly': return 'Every Monday at 8:00 AM';
        case 'none': return 'Disabled';
        default: return '';
      }
    } else {
      switch (frequency) {
        case 'twice_daily': return 'Morning (8:00 AM) and Evening (8:00 PM)';
        case 'daily': return 'Once daily at 8:00 AM';
        case 'weekly': return 'Every Monday at 8:00 AM';
        case 'none': return 'Disabled';
        default: return '';
      }
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">
          <div className="spinner"></div>
          <p>Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>⚙️ Settings</h1>
        <p className="settings-subtitle">Manage your email notification preferences</p>
      </div>

      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      <div className="settings-content">
        {/* Email Notifications Toggle */}
        <div className="settings-section">
          <div className="section-header">
            <h2>📧 Email Notifications</h2>
            <p className="section-description">Enable or disable all email notifications</p>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <label className="setting-label">Enable Email Notifications</label>
              <p className="setting-description">
                Receive automated email reports and updates
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={preferences.emailEnabled}
                onChange={(e) => setPreferences({ ...preferences, emailEnabled: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Daily Briefing Frequency */}
        <div className="settings-section">
          <div className="section-header">
            <h2>📰 Daily Briefing</h2>
            <p className="section-description">Comprehensive summary of issues, stakeholders, and insights</p>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <label className="setting-label">Briefing Frequency</label>
              <p className="setting-description">
                {getFrequencyDescription('briefing', preferences.briefingFrequency)}
              </p>
            </div>
            <select
              className="setting-select"
              value={preferences.briefingFrequency}
              onChange={(e) => setPreferences({ ...preferences, briefingFrequency: e.target.value as any })}
              disabled={!preferences.emailEnabled}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="none">None</option>
            </select>
          </div>

          <div className="setting-preview">
            <strong>What's included:</strong>
            <ul>
              <li>New and updated issues</li>
              <li>Stakeholder activity summary</li>
              <li>Key insights and trends</li>
              <li>Sentiment analysis</li>
            </ul>
          </div>
        </div>

        {/* Monitoring Reports Frequency */}
        <div className="settings-section">
          <div className="section-header">
            <h2>🔍 Monitoring Reports</h2>
            <p className="section-description">Real-time alerts and media monitoring updates</p>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <label className="setting-label">Report Frequency</label>
              <p className="setting-description">
                {getFrequencyDescription('monitoring', preferences.monitoringFrequency)}
              </p>
            </div>
            <select
              className="setting-select"
              value={preferences.monitoringFrequency}
              onChange={(e) => setPreferences({ ...preferences, monitoringFrequency: e.target.value as any })}
              disabled={!preferences.emailEnabled}
            >
              <option value="twice_daily">Twice Daily</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="none">None</option>
            </select>
          </div>

          <div className="setting-preview">
            <strong>What's included:</strong>
            <ul>
              <li>Breaking news and alerts</li>
              <li>Media mentions</li>
              <li>Emerging issues</li>
              <li>Stakeholder updates</li>
            </ul>
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !preferences.emailEnabled && preferences.briefingFrequency === 'none' && preferences.monitoringFrequency === 'none'}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>

        {/* Info Box */}
        <div className="settings-info-box">
          <h3>💡 Need Help?</h3>
          <p>
            Email notifications are sent to your registered email address. 
            All times are in West Africa Time (WAT). You can change these preferences at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
